import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Routes, Route, Outlet } from 'react-router-dom';

import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';

import { DashboardLayout } from '@toolpad/core/DashboardLayout';

import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';

import PermissionRoute from "../navigator/PermissionRoute";
import { MODULES } from "../constants/permissions";

import Login from '../pages/Login';
import ATSInvoices from '../pages/ATSInvoices';
import ShopifyOrders from '../pages/ShopifyOrders';
import WineTasting from '../pages/WineTasting';
import LiquorConnect from '../pages/LiquorConnect';
import WineChart from '../pages/WineChart';
import AGLCPurchaseOrders from '../pages/AGLCPurchaseOrders';
import UpdateCostsCin7 from '../pages/UpdateCostCin7';
import RetailPriceTags from '../pages/RetailPriceTags';
import ProductReception from '../pages/ProductReception';
import FulfillmentAndTracking from '../pages/FulfillmentAndTracking';
import ProductCreator from '../pages/ProductCreator';
import ProductEditor from '../pages/ProductEditor';
import ProductTypes from '../pages/ProductTypes';
import UsersPage from '../pages/UsersPage';
import LocationsPage from '../pages/LocationsPage';

import useAuth from '../hooks/useAuth';

import logo from '../assets/logov2.png';
import ContinueSellingChangeLog from '../pages/ContinueSellingChangeLog';
import BundlesChangeLog from '../pages/BundlesChangeLog';
import UnitsOfMeasure from '../pages/UnitsOfMeasure';
import PresentationsPage from '../pages/PresentationsPage';
import SizesPage from '../pages/SizesPage';
import Bundles from '../pages/Bundles';
import Categories from '../pages/Categories';
import ContainerOptionsPage from '../pages/ContainerOptionsPage';
import ProducersPage from '../pages/ProducersPage';
import ShopifyBackups from '../pages/ShopifyBackups';
import CostUpdateCSVs from '../pages/CostUpdateCSVs';

function CustomToolbarAccount() {
  const auth = useAuth();
  const history = useNavigate();

  const onLogout = () => auth.signout(() => history('/'));

  if (!auth.user) return null;

  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Typography variant="h6">{auth.getFullName()}</Typography>
      <IconButton color="inherit" onClick={onLogout}>
        <PowerSettingsNewIcon />
      </IconButton>
    </Stack>
  );
}

function CustomDashboardLayout() {
  const { pathname } = useLocation();

  function getPageTitle() {
    if (pathname.startsWith('/users')) return 'Users';
    if (pathname.startsWith('/locations')) return 'Locations';
    if (pathname.startsWith('/ats-invoices')) return 'ATS Cost Comparison';
    if (pathname.startsWith('/orders')) return 'Store Pickup Manager';
    if (pathname.startsWith('/wine-chart')) return 'Vintage Chart';
    if (pathname.startsWith('/wine-tasting')) return 'Tastings by ZYN';
    if (pathname.startsWith('/liquor-connect')) return 'AGLC Price Changes';
    if (pathname.startsWith('/cost-update-csvs')) return 'Cost Update CSVs';
    if (pathname.startsWith('/aglc-purchase-orders')) return 'AGLC Purchase Orders';
    if (pathname.startsWith('/update-costs-cin7')) return 'Update Costs (Cin7)';
    if (pathname.startsWith('/retail-price-tags')) return 'Retail Price Tags';
    if (pathname.startsWith('/receiving')) return 'Product Reception';
    if (pathname.startsWith('/fulfillment')) return 'ATS Fulfillment & Tracking';
    if (pathname.startsWith('/product-creator')) return 'Product Creator';
    if (pathname.startsWith('/product-editor')) return 'Product Editor';
    if (pathname.startsWith('/product-types')) return 'Type, Subtype, Subsubtype';
    if (pathname.startsWith('/continue-selling-change-log')) return 'Continue Selling Change Log';
    if (pathname.startsWith('/bundles-change-log')) return 'Bundles Changelog';
    if (pathname.startsWith('/units-of-measure')) return 'Units of Measure';
    if (pathname.startsWith('/presentations')) return 'Presentations';
    if (pathname.startsWith('/sizes')) return 'Sizes';
    if (pathname.startsWith('/categories')) return 'Categories';
    if (pathname.startsWith('/container-options')) return 'Container Options';
    if (pathname.startsWith('/shopify-backups')) return 'Shopify Backups';
    if (pathname.startsWith('/bundles')) return 'Bundles';
    if (pathname.startsWith('/producers')) return 'Producers';
    return '';
  }

  return (
    <DashboardLayout
      slots={{
        toolbarAccount: CustomToolbarAccount,
      }}
      branding={{
        logo: (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 1 }}>
            <img src={logo} alt="ZYN Logo" style={{ height: 32, marginRight: 24 }} />
            <Typography sx={{ color: '#fff', fontWeight: 600 }}>
              {getPageTitle()}
            </Typography>
          </Stack>
        ),
        title: "",
        homeUrl: '/fulfillment',
      }}
    >
      <Outlet />
    </DashboardLayout>
  );
}

export default function Layout() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route element={<CustomDashboardLayout />}>
        <Route path="/" element={<Navigate to="/fulfillment" replace />} />
        <Route path="/users" element={<PermissionRoute module={MODULES.USERS}><UsersPage /></PermissionRoute>}/>
        <Route path="/locations" element={<PermissionRoute module={MODULES.LOCATIONS}><LocationsPage /></PermissionRoute>}/>
        <Route path="/ats-invoices" element={<PermissionRoute module={MODULES.ATS_COST_COMPARISON}><ATSInvoices /></PermissionRoute>}/>
        <Route path="/orders" element={<PermissionRoute module={MODULES.STORE_PICKUP}><ShopifyOrders /></PermissionRoute>}/>
        <Route path="/wine-tasting" element={<PermissionRoute module={MODULES.TASTINGS}><WineTasting /></PermissionRoute>}/>
        <Route path="/wine-chart" element={<PermissionRoute module={MODULES.VINTAGE_CHART}><WineChart /></PermissionRoute>}/>
        <Route path="/liquor-connect" element={<PermissionRoute module={MODULES.AGLC_PRICE_CHANGES}><LiquorConnect /></PermissionRoute>}/>
        <Route path="/cost-update-csvs" element={<PermissionRoute module={MODULES.COST_UPDATE_CSVS}><CostUpdateCSVs /></PermissionRoute>}/>
        <Route path="/aglc-purchase-orders" element={<PermissionRoute module={MODULES.AGLC_PURCHASE_ORDERS}><AGLCPurchaseOrders /></PermissionRoute>}/>
        <Route path="/update-costs-cin7" element={<PermissionRoute module={MODULES.UPDATE_COSTS}><UpdateCostsCin7 /></PermissionRoute>}/>
        <Route path="/retail-price-tags" element={<PermissionRoute module={MODULES.RETAIL_PRICE_TAGS}><RetailPriceTags /></PermissionRoute>}/>
        <Route path="/receiving" element={<PermissionRoute module={MODULES.RECEIVING}><ProductReception /></PermissionRoute>}/>
        <Route path="/fulfillment" element={<PermissionRoute module={MODULES.ATS}><FulfillmentAndTracking /></PermissionRoute>}/>
        <Route path="/product-creator" element={<PermissionRoute module={MODULES.PRODUCT_CREATOR}><ProductCreator /></PermissionRoute>}/>
        <Route path="/units-of-measure" element={<PermissionRoute module={MODULES.UNITS_OF_MEASURE}><UnitsOfMeasure /></PermissionRoute>}/>
        <Route path="/sizes" element={<PermissionRoute module={MODULES.SIZES}><SizesPage /></PermissionRoute>}/>
        <Route path="/container-options" element={<PermissionRoute module={MODULES.CONTAINER_OPTIONS}><ContainerOptionsPage /></PermissionRoute>}/>
        <Route path="/producers" element={<PermissionRoute module={MODULES.PRODUCERS}><ProducersPage /></PermissionRoute>}/>
        <Route path="/product-editor" element={<PermissionRoute module={MODULES.PRODUCT_EDITOR}><ProductEditor /></PermissionRoute>}/>
        <Route path="/product-types" element={<PermissionRoute module={MODULES.PRODUCT_TYPES}><ProductTypes /></PermissionRoute>}/>
        <Route path="/presentations" element={<PermissionRoute module={MODULES.PRESENTATIONS}><PresentationsPage /></PermissionRoute>}/>
        <Route path="/continue-selling-change-log" element={<PermissionRoute module={MODULES.CONTINUE_SELLING}><ContinueSellingChangeLog /></PermissionRoute>} />
        <Route path="/bundles-change-log" element={<PermissionRoute module={MODULES.BUNDLES_CHANGELOG}><BundlesChangeLog /></PermissionRoute>} />
        <Route path="/categories" element={<PermissionRoute module={MODULES.CATEGORIES}><Categories /></PermissionRoute>}/>
        <Route path="/shopify-backups" element={<PermissionRoute module={MODULES.SHOPIFY_BACKUPS}><ShopifyBackups /></PermissionRoute>}/>
        <Route path="/bundles" element={<PermissionRoute module={MODULES.BUNDLES}><Bundles /></PermissionRoute>}/>
      </Route>
    </Routes>
  );
}
